import type { BankTransactionSide, ContractDirection } from '../enums'
import type {
  BankTransactionResponse,
  OpenInvoiceResponse,
} from '../models/bank'
import type { ContractResponse, InvoiceResponse } from '../models/finance'

import {
  grossOf,
  roundMoney,
  sameMoney,
  signOf,
  sumMoney,
  withinRounding,
} from './finance'

/**
 * Why a transaction looks like the payment of an invoice, strongest first.
 * `NUMBER`: the invoice number is in the reference or the counterparty's
 * line. `AMOUNT`: what is left of the transfer is what the invoice has open.
 * `PARTY`: the counterparty is the one the contract names.
 */
export type BankMatchReason = 'NUMBER' | 'AMOUNT' | 'PARTY'

/** How long a normalised name has to be to count as found in another. */
const PARTY_MIN = 6

const WEIGHT: Record<BankMatchReason, number> = {
  NUMBER: 4,
  AMOUNT: 2,
  PARTY: 1,
}

// ── Direction ───────────────────────────────────────────────────────────────

/** 1 or -1: the sign of a payment booked from money moving this way on this
 *  side. Money in pays a receivable contract and refunds a payable one. */
export const paymentSignOfTransaction = (
  side: BankTransactionSide,
  direction: ContractDirection,
): number => ((side === 'CREDIT') === (direction === 'RECEIVABLE') ? 1 : -1)

/** Whether money moving this way can be a payment of that sign on that side:
 *  money in on a customer invoice, money out on a Firma's, and the other way
 *  round where the invoice is owed back. */
export const movesAs = (
  side: BankTransactionSide,
  direction: ContractDirection,
  paymentSign: number,
): boolean => paymentSignOfTransaction(side, direction) === paymentSign

// ── The transaction ─────────────────────────────────────────────────────────

/** Gross, EUR: what of a transfer no payment books yet. */
export const transactionLeft = (
  amount: number,
  payments: readonly { bankAmount: number | null }[],
): number =>
  Math.max(
    0,
    roundMoney(amount - sumMoney(payments.map(p => p.bankAmount ?? 0))),
  )

/** The inbox's piles: still to book, booked, set aside. */
export const BANK_VIEWS = ['OPEN', 'BOOKED', 'IGNORED'] as const
export type BankView = (typeof BANK_VIEWS)[number]

/** The pile a transaction lies in. A declined one is in none: the money
 *  never moved. A reversed one with payments booked on it is open until
 *  they are removed, since the invoices count money that came back. */
export function bankViewOf(
  transaction: Pick<
    BankTransactionResponse,
    'ignoredAt' | 'status' | 'left' | 'bookedOn'
  >,
): BankView | null {
  if (transaction.ignoredAt) return 'IGNORED'
  if (transaction.status === 'DECLINED') return null
  if (transaction.status === 'REVERSED') {
    return transaction.bookedOn.length > 0 ? 'OPEN' : 'BOOKED'
  }
  return sameMoney(transaction.left, 0) ? 'BOOKED' : 'OPEN'
}

/** Whether a transaction can still back a payment: the bank completed it,
 *  nobody set it aside, and some of it is left. */
export const isBookable = (
  transaction: Pick<BankTransactionResponse, 'status' | 'ignoredAt' | 'left'>,
): boolean =>
  transaction.status === 'COMPLETED' &&
  transaction.ignoredAt == null &&
  !sameMoney(transaction.left, 0)

// ── The invoice ─────────────────────────────────────────────────────────────

/** What of an invoice a payment and the matching read: its figures with its
 *  corrections, and whether the money moves against it. */
export type PaymentTarget = Pick<
  OpenInvoiceResponse,
  | 'number'
  | 'invoiceDate'
  | 'direction'
  | 'partyName'
  | 'vatRate'
  | 'open'
  | 'paymentSign'
  | 'refund'
>

/** An invoice of its contract as a payment target. */
export const paymentTargetOf = (
  invoice: Pick<
    InvoiceResponse,
    | 'number'
    | 'invoiceDate'
    | 'vatRate'
    | 'netAmount'
    | 'openWithCorrections'
    | 'paymentSign'
  >,
  contract: Pick<ContractResponse, 'direction' | 'partyName'>,
): PaymentTarget => ({
  number: invoice.number,
  invoiceDate: invoice.invoiceDate,
  direction: contract.direction,
  partyName: contract.partyName,
  vatRate: invoice.vatRate,
  open: invoice.openWithCorrections,
  paymentSign: invoice.paymentSign,
  // A correction has it owed back: the money moves against the invoice.
  refund: invoice.paymentSign !== signOf(invoice.netAmount),
})

/** Whether a payment can still be booked on the invoice: it counts, it is
 *  not a correction — the invoice a correction raises or lowers is the one
 *  paid — and it and its corrections have something open. */
export const awaitsPayment = (
  invoice: Pick<
    InvoiceResponse,
    'status' | 'correctsInvoiceId' | 'openWithCorrections'
  >,
): boolean =>
  invoice.status !== 'CANCELLED' &&
  invoice.correctsInvoiceId == null &&
  !sameMoney(invoice.openWithCorrections, 0)

/** A contract as the payment targets read it: its side, its other party,
 *  whether it is active, and its invoices. */
type TargetContract<I> = Pick<
  ContractResponse,
  'status' | 'direction' | 'partyName'
> & { invoices: readonly I[] }

type TargetInvoice = Parameters<typeof paymentTargetOf>[0] &
  Parameters<typeof awaitsPayment>[0]

/** Every invoice of the active contracts a payment can still be booked on,
 *  as a payment target beside its contract and invoice. */
export const paymentTargetsOf = <C extends TargetContract<TargetInvoice>>(
  contracts: readonly C[],
): (PaymentTarget & { contract: C; invoice: C['invoices'][number] })[] =>
  contracts
    .filter(contract => contract.status === 'ACTIVE')
    .flatMap(contract =>
      contract.invoices.filter(awaitsPayment).map(invoice => ({
        ...paymentTargetOf(invoice, contract),
        contract,
        invoice,
      })),
    )

/** Gross, EUR, positive: what the invoice and its corrections have open. */
export const openGrossOf = (invoice: Pick<PaymentTarget, 'open' | 'vatRate'>) =>
  Math.abs(grossOf(invoice.open, invoice.vatRate))

/** Gross, EUR: what a payment on the invoice books of the transfer — what is
 *  left of it, at most what the invoice has open; all of it where it misses
 *  that by no more than rounding, so no stray cent stays on either side. */
export function bookableGross(
  transaction: Pick<BankTransactionResponse, 'left'>,
  invoice: Pick<PaymentTarget, 'open' | 'vatRate'>,
): number {
  const open = openGrossOf(invoice)
  return withinRounding(transaction.left, open)
    ? transaction.left
    : roundMoney(Math.min(transaction.left, open))
}

// ── Matching ────────────────────────────────────────────────────────────────

/** Letters and digits only, lower-cased: how a number or a name is compared
 *  whatever the bank did to spaces, dashes and case. */
const normalize = (text: string | null | undefined) =>
  (text ?? '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '')

const isDigit = (char: string | undefined) => char != null && /\d/.test(char)

/** A text normalised, with the offsets at which a word starts: a bank may
 *  space a number any way, but digits a space or a list separator keeps apart
 *  are not one number. A dash joins, as in `R-00127-2`; a point and a comma
 *  stand inside dates and amounts. */
type ScannedText = { chars: string; wordStarts: ReadonlySet<number> }

function scan(text: string | null | undefined): ScannedText {
  let chars = ''
  const wordStarts = new Set<number>()
  for (const word of (text ?? '').split(/[\s/;:_]+/)) {
    wordStarts.add(chars.length)
    chars += normalize(word)
  }
  return { chars, wordStarts }
}

/** Whether `needle`, normalised, stands in `text` as itself — `r00127` in
 *  "Rechnung R-00127", not in "R-001270"; `202415` in "3 202415", not in
 *  "3202415". */
function containsNumber(text: ScannedText, needle: string): boolean {
  if (needle.length < 3) return false
  const { chars, wordStarts } = text
  const stopsAt = (edge: number, neighbour: number) =>
    wordStarts.has(edge) || !isDigit(chars[neighbour])
  for (
    let at = chars.indexOf(needle);
    at >= 0;
    at = chars.indexOf(needle, at + 1)
  ) {
    const end = at + needle.length
    if (stopsAt(at, at - 1) && stopsAt(end, end)) return true
  }
  return false
}

type MatchTransaction = Pick<
  BankTransactionResponse,
  'side' | 'left' | 'reference' | 'counterparty'
>

/** A transaction's text, normalised once for every invoice it is held
 *  against. Reference and counterparty stay apart: joined, the digits at
 *  the end of one would run into those at the start of the other. */
const textOf = (transaction: MatchTransaction) => ({
  reference: scan(transaction.reference),
  counterparty: scan(transaction.counterparty),
})

function reasonsOf(
  transaction: MatchTransaction,
  text: ReturnType<typeof textOf>,
  invoice: PaymentTarget,
): BankMatchReason[] {
  const reasons: BankMatchReason[] = []
  const number = normalize(invoice.number)
  if (
    containsNumber(text.reference, number) ||
    containsNumber(text.counterparty, number)
  ) {
    reasons.push('NUMBER')
  }
  if (withinRounding(openGrossOf(invoice), transaction.left)) {
    reasons.push('AMOUNT')
  }
  if (reasons.length === 0) return []
  // Either name may carry more than the other ("Meier GmbH" against
  // "Holzbau Meier GmbH"), but the one found inside has to name somebody: a
  // short word like "bau" or "gmbh" stands in many names.
  const party = normalize(invoice.partyName)
  const counterparty = text.counterparty.chars
  if (
    (party.length >= PARTY_MIN && counterparty.includes(party)) ||
    (counterparty.length >= PARTY_MIN && party.includes(counterparty))
  ) {
    reasons.push('PARTY')
  }
  return reasons
}

/**
 * Why `transaction` looks like the payment of `invoice`; empty where it
 * cannot be — the money moves the other way — or nothing points at it. The
 * counterparty alone is no reason: a Firma has many invoices.
 */
export const matchReasons = (
  transaction: MatchTransaction,
  invoice: PaymentTarget,
): BankMatchReason[] =>
  movesAs(transaction.side, invoice.direction, invoice.paymentSign)
    ? reasonsOf(transaction, textOf(transaction), invoice)
    : []

const scoreOf = (reasons: readonly BankMatchReason[]) =>
  reasons.reduce((sum, reason) => sum + WEIGHT[reason], 0)

/** A candidate with the reasons it is one; none where it only fits. */
export type BankMatch<T> = { item: T; reasons: BankMatchReason[] }

/** Stronger first; on a tie, the older first. */
const byStrength =
  <T>(dateOf: (candidate: T) => string) =>
  (a: BankMatch<T>, b: BankMatch<T>): number =>
    scoreOf(b.reasons) - scoreOf(a.reasons) ||
    dateOf(a.item).localeCompare(dateOf(b.item))

/** Every candidate, the ones with reasons first by strength. */
function ranked<T>(
  candidates: readonly T[],
  reasonsFor: (candidate: T) => BankMatchReason[],
  dateOf: (candidate: T) => string,
): BankMatch<T>[] {
  return candidates
    .map(item => ({ item, reasons: reasonsFor(item) }))
    .sort(byStrength(dateOf))
}

/** The first of a ranked list, where anything points at it: what a surface
 *  suggests. One that only fits is no suggestion. */
export const strongestMatch = <T>(
  matches: readonly BankMatch<T>[],
): BankMatch<T> | null => {
  const [first] = matches
  return first && first.reasons.length > 0 ? first : null
}

/** The invoice a transaction points at most strongly, where it points at any:
 *  what `invoicesFor` ranks first, without ranking the rest. */
export function bestInvoiceFor<T extends PaymentTarget>(
  transaction: MatchTransaction,
  invoices: readonly T[],
): BankMatch<T> | null {
  const text = textOf(transaction)
  const stronger = byStrength<T>(invoice => invoice.invoiceDate)
  let best: BankMatch<T> | null = null
  for (const item of invoices) {
    if (!movesAs(transaction.side, item.direction, item.paymentSign)) continue
    const reasons = reasonsOf(transaction, text, item)
    if (reasons.length === 0) continue
    const match = { item, reasons }
    if (!best || stronger(match, best) < 0) best = match
  }
  return best
}

/** The open invoices a transaction can pay — those it points at first. */
export function invoicesFor<T extends PaymentTarget>(
  transaction: MatchTransaction,
  invoices: readonly T[],
): BankMatch<T>[] {
  const text = textOf(transaction)
  return ranked(
    invoices.filter(invoice =>
      movesAs(transaction.side, invoice.direction, invoice.paymentSign),
    ),
    invoice => reasonsOf(transaction, text, invoice),
    invoice => invoice.invoiceDate,
  )
}

/** The bookable transactions that can pay an invoice — those that point at
 *  it first. */
export const transactionsFor = <
  T extends MatchTransaction &
    Pick<BankTransactionResponse, 'emittedAt' | 'status' | 'ignoredAt'>,
>(
  invoice: PaymentTarget,
  transactions: readonly T[],
): BankMatch<T>[] =>
  ranked(
    transactions.filter(
      t =>
        isBookable(t) &&
        movesAs(t.side, invoice.direction, invoice.paymentSign),
    ),
    t => reasonsOf(t, textOf(t), invoice),
    t => t.emittedAt,
  )
